import { useEffect, useState, useCallback, useMemo } from 'react'
import { useLocation, useOutletContext, useParams } from 'react-router-dom'
import { Box, Typography, Fade } from '@mui/material'
import columnsConfig from '@/components/features/config/columns/consumers/column.AnalyticsConsumerTable.config'
import DataGridPremiumDemo from '@/components/Table/DataGrid'
import SearchBar from '@/components/common/SearchBar'
import { useSearch } from '@/hooks/useSearch'
import AddConsumerPanel from './AddConsumerPanel'
import ChangeRolePanel from './ChangeRolePanel'
import DeleteConsumerPanel from './DeleteConsumerPanel'
import AnalyticsDrawerContent from './AnalyticsDrawerContent'
import Success from '@assets/Analytics/Success.svg'
import { useAppTheme } from '@/theme/ThemeContext'
import {
  addWaterTreeConsumers,
  removeWaterTreeConsumers,
  getWaterTreeZoneConsumers,
  setWaterTreeConsumersRole,
} from '@/services/dashboardService'
import { ConsumerRow } from '@/services/consumerService'
import type { TreeMemberRole } from '@/services/waterTreeMemberRoles'
import { useScopedApiFilters } from '@/store/table/table'
import { getServerPaginationUpdate, toApiPage } from '@/utils/serverPagination'

type Notice = { tone: 'success' | 'error'; text: string }

const NOTICE_STYLES = {
  success: {
    role: 'status',
    icon: Success,
    color: '#10B981',
    border: 'rgba(16, 185, 129, 0.2)',
    light: '#F0FDF4',
    dark: 'rgba(16, 185, 129, 0.1)',
  },
  error: {
    role: 'alert',
    icon: null,
    color: '#EF4444',
    border: 'rgba(239, 68, 68, 0.2)',
    light: '#FEF2F2',
    dark: 'rgba(239, 68, 68, 0.1)',
  },
} as const

export default function AnalyticsConsumerTable() {
  // The tab filters its own members, not the trees list behind it.
  const apiFilters = useScopedApiFilters('tree-consumers')
  const { consumerId } = useParams()
  const location = useLocation()
  const outletContext = useOutletContext<{ zoneType?: string; treeName?: string }>()
  const queryZoneType = new URLSearchParams(location.search).get('zoneType')
  const zoneType =
    Number(queryZoneType ?? outletContext?.zoneType ?? 2) === 1 ? 1 : 2
  const [consumers, setConsumers] = useState<ConsumerRow[]>([])
  const [totalConsumers, setTotalConsumers] = useState(0)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<Notice | null>(null)
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  const accountId = localStorage.getItem('accountId') ?? ''
  const [selectedRowIds, setSelectedRowIds] = useState<Array<string | number>>(
    []
  )

  // A new search starts on the first page, and rows that the search hides leave the selection,
  // so the role and remove panels never act on a member the operator cannot see.
  const search = useSearch({
    onQueryChange: () => {
      setPage(0)
      setSelectedRowIds([])
    },
  })
  // Column filters change the row set like a search does.
  useEffect(() => {
    setPage(0)
    setSelectedRowIds([])
  }, [apiFilters])
  const { run: runSearch, query: searchText } = search

  const fetchConsumers = useCallback(() => {
    if (!accountId || !consumerId) return
    setError(null)
    runSearch(
      signal =>
        getWaterTreeZoneConsumers(
          {
            accountId,
            zoneId: consumerId,
            zoneType,
            page: toApiPage(page),
            size: pageSize,
            sortBy: 'premiseId',
            sortOrder: 'DESC',
          },
          [
            ...apiFilters,
            ...(searchText ? [{ name: 'search', value: [searchText] }] : []),
          ],
          signal
        ),
      ({ rows, total }) => {
        setConsumers(rows)
        setTotalConsumers(total)
      },
      err => {
        console.error('[AnalyticsConsumerTable] fetch consumers:', err)
        setConsumers([])
        setTotalConsumers(0)
        setError('שגיאה בטעינת הנתונים')
      }
    )
  }, [accountId, consumerId, page, pageSize, zoneType, searchText, apiFilters, runSearch])

  useEffect(() => {
    fetchConsumers()
  }, [fetchConsumers])

  const handlePaginationChange = useCallback(
    (newPage: number, newPageSize: number) => {
      const next = getServerPaginationUpdate(pageSize, newPage, newPageSize)
      setPage(next.page)
      setPageSize(next.pageSize)
    },
    [pageSize]
  )

  // A failure line stays until the next add or role change; a success line
  // fades on its own.
  useEffect(() => {
    if (notice?.tone !== 'success') return
    const timer = setTimeout(() => setNotice(null), 3000)
    return () => clearTimeout(timer)
  }, [notice])

  const rowSelectionModel = useMemo(
    () => ({ type: 'include' as const, ids: new Set(selectedRowIds) }),
    [selectedRowIds]
  )

  const handleRowClick = (row: any) => {
    setSelectedRowIds(prev =>
      prev.includes(row.id)
        ? prev.filter(id => id !== row.id)
        : [...prev, row.id]
    )
  }

  const handleAdd = async (premiseIds: number[], role: TreeMemberRole) => {
    if (!accountId || !consumerId || premiseIds.length === 0) return
    setNotice(null)
    try {
      const count = await addWaterTreeConsumers(
        accountId,
        consumerId,
        premiseIds,
        role,
        zoneType
      )
      setNotice({ tone: 'success', text: `נוספו ${count} צרכנים בהצלחה!` })
      fetchConsumers()
    } catch (err: any) {
      console.error(
        '[AnalyticsConsumerTable] add consumers failed:',
        err.response?.data ?? err
      )
      setNotice({ tone: 'error', text: 'הוספת הצרכנים נכשלה' })
    }
  }

  const selectedPremiseIds = () =>
    selectedRowIds.map(id => Number(id)).filter(n => !Number.isNaN(n))

  const handleChangeRole = async (role: TreeMemberRole) => {
    if (!accountId || !consumerId || selectedRowIds.length === 0) return
    setNotice(null)
    try {
      const count = await setWaterTreeConsumersRole(
        accountId,
        consumerId,
        selectedPremiseIds(),
        role,
        zoneType
      )
      setSelectedRowIds([])
      setNotice({ tone: 'success', text: `עודכן התפקיד של ${count} צרכנים` })
      fetchConsumers()
    } catch (err) {
      console.error('[AnalyticsConsumerTable] change role failed:', err)
      setNotice({ tone: 'error', text: 'עדכון התפקיד נכשל' })
    }
  }

  const handleRemove = async () => {
    if (!accountId || !consumerId || selectedRowIds.length === 0) return
    const premiseIds = selectedPremiseIds()
    try {
      await removeWaterTreeConsumers(
        accountId,
        consumerId,
        premiseIds,
        zoneType
      )
      setSelectedRowIds([])
      fetchConsumers()
    } catch (err: any) {
      console.error(
        '[AnalyticsConsumerTable] remove consumers failed:',
        err.response?.data ?? err
      )
    }
  }

  const shownNotice = notice && {
    ...NOTICE_STYLES[notice.tone],
    message: notice.text,
  }

  return (
    <Box
      sx={{
        width: '100%',
        direction: 'rtl',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        px: 0,
        pb: 1,
      }}
    >
      {shownNotice && (
        <Box sx={{ height: 32, display: 'flex', justifyContent: 'flex-start' }}>
          <Fade in>
            <Box
              role={shownNotice.role}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                bgcolor: isDarkMode ? shownNotice.dark : shownNotice.light,
                px: 3,
                py: 0.6,
                borderRadius: '8px',
                border: `1px solid ${shownNotice.border}`,
              }}
            >
              {shownNotice.icon && (
                <img
                  src={shownNotice.icon}
                  alt="success"
                  style={{ width: 16 }}
                />
              )}
              <Typography
                sx={{
                  color: shownNotice.color,
                  fontWeight: 600,
                  fontSize: '15px',
                  fontFamily: 'Rubik, sans-serif',
                }}
              >
                {shownNotice.message}
              </Typography>
            </Box>
          </Fade>
        </Box>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 460px', xl: '1fr 480px' },
          gap: { xs: 2, lg: 3 },
          alignItems: 'start',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <SearchBar
            placeholder="חיפוש לפי מספר מד, שם צרכן, כתובת או כרטיס מים"
            width="clamp(220px, 100%, 380px)"
            height={34}
            onSearch={search.setText}
            executeSearch={search.searchNow}
            sx={{
              mb: 1.5,
              bgcolor: isDarkMode ? '#091327' : '#FFFFFF',
              border: isDarkMode ? 'none' : '1px solid #E6ECF2',
            }}
          />
          <Box
            sx={{
              height: 'calc(100vh - 261px)',
              minHeight: 450,
              borderRadius: 0,
              overflow: 'hidden',
              border: isDarkMode ? '1px solid #323B77' : '1px solid #EAEDF2',
              bgcolor: isDarkMode ? '#202B40' : '#FFFFFF',
            }}
          >
            {error ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <Typography color="error">{error}</Typography>
              </Box>
            ) : (
              <DataGridPremiumDemo
                checkboxSelection
                columns={columnsConfig}
                rows={consumers}
                hideFooter={true}
                headerPosition="bottom"
                pageSize={pageSize}
                serverSideRowCount={totalConsumers}
                serverSidePage={page}
                onPaginationChange={handlePaginationChange}
                paginationMode="server"
                filterMode="server"
                loading={search.isSearching}
                onRowClick={handleRowClick}
                rowSelectionModel={rowSelectionModel}
                onRowSelectionModelChange={model => {
                  setSelectedRowIds(
                    model.type === 'include' ? Array.from(model.ids) : []
                  )
                }}
                rowHeight={42}
                columnHeaderHeight={48}
                maxHeight="100%"
              />
            )}
          </Box>
        </Box>

        <Box
          sx={{
            width: '100%',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5,
          }}
        >
          <AddConsumerPanel
            title="הוספת צרכן"
            width="100%"
            drawerContent={AnalyticsDrawerContent}
            onAdd={handleAdd}
            accountId={accountId}
            zoneId={consumerId}
            zoneType={zoneType}
            consumerRows={consumers}
          />
          <ChangeRolePanel
            selectedCount={selectedRowIds.length}
            onChangeRole={handleChangeRole}
          />
          <DeleteConsumerPanel
            width="100%"
            title="הסרת צרכנים מעץ המים"
            selectedRowId={selectedRowIds}
            treeName={outletContext?.treeName}
            onRemove={handleRemove}
          />
        </Box>
      </Box>
    </Box>
  )
}
